/**
 * `useMerchants` — the merchant directory, read once and shared.
 *
 * The React half of `src/lib/merchants.ts`. Every screen that filters by
 * merchant calls this, and they all read the one cache entry: the query key is
 * a constant because the endpoint takes no parameters, so four reports open in
 * a session cost one request rather than four.
 *
 * `staleTime` is an hour. The directory is a list of merchant organizations —
 * a new one appears when sales onboards one, not while a reader is looking at
 * a report — and refetching it on every mount would put a request in front of
 * every page for a list that has not changed. A reader who needs a merchant
 * onboarded minutes ago is served by the picker accepting a raw id, not by a
 * shorter window.
 *
 * It lives in `src/hooks/` rather than `src/lib/` because it holds React
 * Query's state, and not in `src/routes/` because every file there becomes a
 * route.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { fetchMerchants, type MerchantDirectory } from '../lib/merchants.ts'

/** The one cache key. A constant, because the endpoint takes no parameters. */
export const MERCHANTS_QUERY_KEY = ['merchants'] as const

/** How long a fetched directory is served without asking again. */
export const MERCHANTS_STALE_TIME_MS = 60 * 60 * 1000

/**
 * Read the merchant directory.
 *
 * A failure is not thrown at the screen. Every caller uses this to populate a
 * filter, and a report whose figures are fine must not be replaced by an error
 * because the list beside it could not load — the picker says so on itself and
 * still accepts a typed id.
 *
 * @returns The query, whose `data` is the directory once it has arrived.
 */
export function useMerchants(): UseQueryResult<MerchantDirectory> {
  return useQuery({
    queryKey: MERCHANTS_QUERY_KEY,
    queryFn: fetchMerchants,
    staleTime: MERCHANTS_STALE_TIME_MS,
    retry: false
  })
}
